import { Children, cloneElement, Component } from "react";
import classNames from "classnames";

import { store } from "common/store";
import { amount, number } from "common/formats";
import { Placeholder } from "common/components/placeholder";

export class SummaryBar extends Component {
    static Settlements = (props) => (props.section === "settlements" ? props.children : null);

    static Chargebacks = (props) => (props.section === "chargebacks" ? props.children : null);

    static Fees = (props) => (props.section === "fees" ? props.children : null);

    static Misc = (props) => (props.section === "misc" ? props.children : null);

    static Totals = (props) => <div className="section-totals">{props.children}</div>;

    static Amount = (props) => <div className="amount">{props.children}</div>;

    static Count = (props) => <div className="count">{props.children}</div>;

    static Detail = (props) => <div className="detail">{props.children}</div>;

    static Title = (props) => <div className={props.className}>{props.text}</div>;

    static Data = (props) => <div className="data">{props.loading ? <Placeholder /> : props.children}</div>;

    static Details = ({ schema, ...rest }) => (
        <div className="section-details">
            <div className="description">
                <div className="header">{schema.description?.header || ""}</div>
                {schema.description.values.map((item) => (
                    item === "_blank_" ? <div key={item} className="item blank" >&nbsp;</div> : <div key={item} className="item">{item}</div>
                ))}
            </div>
            <div className="amount">
                <div className="header">{schema.amount?.header || "Amount"}</div>
                {/* safe to use index as array does not change */}
                {/* eslint-disable-next-line react/no-array-index-key */}
                {schema.amount.values.map(({ value, currency }, index) => (rest.loading ? <Placeholder /> : <div key={index} className="item">{amount(value, { currency })}</div>))}
            </div>
            {schema.count && (
            <div className="count">
                <div className="header">{schema.count?.header || "Count"}</div>
                {/* eslint-disable-next-line react/no-array-index-key */}
                {schema.count.values.map((item, index) => (rest.loading ? <Placeholder /> : <div key={index} className="item">{number(item)}</div>))}
            </div>
            )}
        </div>
    );

    render() {
        return (
            <div className={classNames("batch-summary-bar", this.props.section)}>
                {Children.map(this.props.children, (child) => cloneElement(child, { section: this.props.section }))}
            </div>
        );
    }
}

const prepareSettlementsSchema = (summaryData) => {
    const schema = {
        description: {
            values: ["Sales", "Refunds"]
        },
        amount: {
            values: [
                { value: summaryData?.sale_amount, currency: summaryData?.stl_currency },
                { value: summaryData?.refund_amount, currency: summaryData?.stl_currency }
            ]
        },
        count: {
            values: [summaryData?.sale_count, summaryData?.refund_count]
        }
    };
    if (store.getState().common.merchant_capabilities.has_oct) {
        schema.description.values = [...schema.description.values, "Payouts"];
        schema.amount.values = [...schema.amount.values, { value: summaryData?.payout_amount, currency: summaryData?.stl_currency }];
        schema.count.values = [...schema.count.values, summaryData?.payout_count];
    }
    return schema;
};

export function BatchSummaryBar({ summaryData, section, loading } = {}) {
    return (
        <SummaryBar section={section}>
            <SummaryBar.Settlements>
                <SummaryBar.Totals>
                    <SummaryBar.Amount>
                        <SummaryBar.Title className="header" text={store.getState().common.merchant_capabilities.has_oct ? "Net sales, refunds and payouts" : "Net sales and refunds"} />
                        <SummaryBar.Data loading={loading}>{amount(summaryData?.net_sale_refund_and_payout_amount, { currency: summaryData?.stl_currency })}</SummaryBar.Data>
                    </SummaryBar.Amount>
                    <SummaryBar.Count>
                        <SummaryBar.Title className="header" text="Total transactions" />
                        <SummaryBar.Data loading={loading}>{number(summaryData?.net_sale_refund_and_payout_count)}</SummaryBar.Data>
                    </SummaryBar.Count>
                </SummaryBar.Totals>
                <SummaryBar.Details schema={prepareSettlementsSchema(summaryData)} />
            </SummaryBar.Settlements>
            <SummaryBar.Chargebacks>
                <SummaryBar.Totals>
                    <SummaryBar.Amount>
                        <SummaryBar.Title className="header" text="Net chargebacks" />
                        <SummaryBar.Data loading={loading}>{amount(summaryData?.net_chargeback_amount, { currency: summaryData?.stl_currency })}</SummaryBar.Data>
                    </SummaryBar.Amount>
                    <SummaryBar.Count>
                        <SummaryBar.Title className="header" text="Total transactions" />
                        <SummaryBar.Data loading={loading}>{number(summaryData?.net_chargeback_count)}</SummaryBar.Data>
                    </SummaryBar.Count>
                </SummaryBar.Totals>
                <SummaryBar.Details schema={{
                    description: {
                        header: "Disputes",
                        values: ["1st chargebacks", "Add'l disputes"]
                    },
                    amount: {
                        values: [
                            { value: summaryData?.initial_dispute_amount, currency: summaryData?.stl_currency },
                            { value: summaryData?.additional_dispute_amount, currency: summaryData?.stl_currency }
                        ]
                    },
                    count: {
                        values: [summaryData?.initial_dispute_count, summaryData?.additional_dispute_count]
                    }
                }}
                />
                <SummaryBar.Details schema={{
                    description: {
                        header: "Challenges",
                        values: ["Representments", "Add'l challenges"]
                    },
                    amount: {
                        values: [
                            { value: summaryData?.initial_challenge_amount, currency: summaryData?.stl_currency },
                            { value: summaryData?.additional_challenge_amount, currency: summaryData?.stl_currency }
                        ]
                    },
                    count: {
                        values: [summaryData?.initial_challenge_count, summaryData?.additional_challenge_count]
                    }
                }}
                />
                <SummaryBar.Details schema={{
                    description: {
                        values: ["Other", "_blank_"]
                    },
                    amount: {
                        values: [{ value: summaryData?.other_chargeback_amount, currency: summaryData?.stl_currency }]
                    },
                    count: {
                        values: [summaryData?.other_chargeback_count]
                    }
                }}
                />
            </SummaryBar.Chargebacks>
            <SummaryBar.Fees>
                <SummaryBar.Totals>
                    <SummaryBar.Amount>
                        <SummaryBar.Title className="header" text="Total fees and taxes" />
                        <SummaryBar.Data loading={loading}>{amount(summaryData?.net_fee_and_tax_amount, { currency: summaryData?.stl_currency })}</SummaryBar.Data>
                    </SummaryBar.Amount>
                    <SummaryBar.Amount>
                        <SummaryBar.Title className="header" text="Total fees" />
                        <SummaryBar.Data loading={loading}>{amount(summaryData?.net_fee_amount, { currency: summaryData?.stl_currency })}</SummaryBar.Data>
                    </SummaryBar.Amount>
                    <SummaryBar.Amount>
                        <SummaryBar.Title className="header" text="Total taxes" />
                        <SummaryBar.Data loading={loading}>{amount(summaryData?.tax_amount, { currency: summaryData?.stl_currency })}</SummaryBar.Data>
                    </SummaryBar.Amount>
                </SummaryBar.Totals>
                <SummaryBar.Details schema={{
                    description: {
                        header: "Fee type",
                        values: ["Processor", "Assessment", "Interchange"]
                    },
                    amount: {
                        values: [
                            { value: summaryData?.processor_fee_amount, currency: summaryData?.stl_currency },
                            { value: summaryData?.assessment_fee_amount, currency: summaryData?.stl_currency },
                            { value: summaryData?.interchange_fee_amount, currency: summaryData?.stl_currency }
                        ]
                    }
                }}
                />
            </SummaryBar.Fees>
            <SummaryBar.Misc>
                <SummaryBar.Totals>
                    <SummaryBar.Amount>
                        <SummaryBar.Title className="header" text="Total misc" />
                        <SummaryBar.Data loading={loading}>{amount(summaryData?.net_adjustment_amount, { currency: summaryData?.stl_currency })}</SummaryBar.Data>
                    </SummaryBar.Amount>
                    <SummaryBar.Count>
                        <SummaryBar.Title className="header" text="Misc transactions" />
                        <SummaryBar.Data loading={loading}>{number(summaryData?.net_adjustment_count)}</SummaryBar.Data>
                    </SummaryBar.Count>
                </SummaryBar.Totals>
            </SummaryBar.Misc>
        </SummaryBar>
    );
}
